<script setup lang="ts">
import { ref, watch, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import type { ConfigModule } from '~/types/config';
import { removeColon } from '~/Common/StringOperations';
import { flattenConfigModules } from '~/Common/ConfigHelpers';

interface Props {
  visible: boolean;
  missingFields: ConfigModule[];
}

const props = defineProps<Props>();

const emit = defineEmits<{
  'update:visible': [visible: boolean];
  'navigate-to-element': [{ moduleId: number; xPath: string, elementId: number; }];
}>();

const { t } = useI18n();

// Local state for the dialog visibility
const dialogVisible = ref(props.visible);

// Synchronize local state with the prop
watch(() => props.visible, (newValue) => {
  dialogVisible.value = newValue;
});

// Synchronize changes back to the parent
watch(dialogVisible, (newValue) => {
  if (newValue !== props.visible) {
    emit('update:visible', newValue);
  }
});

// Flatten the missing fields for simpler display
const flattenedMissingFields = computed(() => {
  return flattenConfigModules(props.missingFields);
});

// Function to navigate to a specific missing element
const navigateToElement = (moduleId: number, xPath: string, elementId: number) => {
  // Close dialog and emit navigation event
  dialogVisible.value = false;
  emit('navigate-to-element', { moduleId, xPath, elementId });
};
</script>

<template>
  <Dialog
    :visible="dialogVisible"
    @update:visible="dialogVisible = $event"
    :header="$t('common.dialogs.missingFields.title')"
    modal
    dismissable-mask
    class="w-full"
    :style="{ maxWidth: '600px', width: '90vw' }"
  >
    <div class="p-3">
      <Message
        v-if="flattenedMissingFields.length === 0"
        severity="success"
        :closable="false"
        class="mb-3"
      >
        {{ $t('common.dialogs.missingFields.allFilled') }}
      </Message>

      <div v-else>
        <Message
          severity="error"
          :closable="false"
          class="mb-3"
        >
          {{ $t('common.dialogs.missingFields.description') }}
        </Message>

        <!-- Simple list for few entries -->
        <div class="grid gap-2">
          <div
            v-for="field in flattenedMissingFields"
            :key="field.id"
            class="col-12 p-3 border-1 border-300 border-round surface-50"
          >
            <div class="flex align-items-center justify-content-between">
              <div class="flex-1">
                <div class="flex align-items-center gap-2 mb-1">
                  <i class="pi pi-exclamation-triangle text-red-600" />
                  <span class="font-semibold text-red-600">
                    {{ removeColon(field.translation) || removeColon(field.elementName) }}
                  </span>
                </div>
                <div class="text-sm text-600">
                  <span class="font-medium">
                    {{ field.moduleName }}
                  </span>
                  <i class="pi pi-angle-right mx-1 text-600" />
                  <span>
                    {{ field.categoryName }}
                  </span>
                  <i class="pi pi-angle-right mx-1 text-600" />
                  <span v-if="field.headline" class="ml-2 text-600">
                    {{ field.headline }}
                  </span>
                </div>
              </div>
              <Button
                severity="info"
                type="button"
                icon="pi pi-external-link"
                size="small"
                @click="navigateToElement(field.moduleId, field.elementPath, field.id)"
                v-tooltip.top="$t('common.dialogs.changes.goToField')"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  </Dialog>
</template>
